/**
 * Hook for managing Lambda/pipeline selection state.
 * Handles individual lambda selection, pipeline-level selection (across accounts),
 * stack-level selection, and computed selection metadata.
 */
import { useState, useCallback, useMemo, useRef, startTransition } from 'react';
import { Account, LambdaFunction, Pipeline, SelectedVersions } from '../types';
import { RUNTIME_CONFIGS, getLambdaCountByLanguage } from '../config';

export const useResourceSelection = (accounts: Account[]) => {
  const [selectedLambdaIds, setSelectedLambdaIds] = useState<Set<string>>(new Set());
  const selectedLambdaIdsRef = useRef<Set<string>>(selectedLambdaIds);
  selectedLambdaIdsRef.current = selectedLambdaIds;

  // Counter that increments on every selection change — used to trigger re-reads from the ref
  const [selectionVersion, setSelectionVersion] = useState(0);

  // Toggle individual lambda selection
  const toggleLambdaSelection = useCallback((lambdaId: string) => {
    startTransition(() => {
      setSelectedLambdaIds((prev) => {
        const next = new Set(prev);
        if (next.has(lambdaId)) {
          next.delete(lambdaId);
        } else {
          next.add(lambdaId);
        }
        return next;
      });
      setSelectionVersion((v) => v + 1);
    });
  }, []);

  /**
   * Toggle pipeline selection.
   * For regular pipelines and Manual Deployed Stacks: select ALL lambdas across ALL accounts with same pipeline name.
   * For "Manually created": select ONLY lambdas within that specific pipeline instance (scoped to account).
   */
  const togglePipelineSelection = useCallback(
    (pipeline: Pipeline, e: React.MouseEvent) => {
      e.stopPropagation();
      const pipelineName = pipeline.name;
      const allLambdaIdsToToggle: string[] = [];

      if (pipelineName === 'Manually created') {
        pipeline.lambdas.forEach((l) => allLambdaIdsToToggle.push(l.id));
      } else {
        accounts.forEach((account) => {
          account.pipelines.forEach((p) => {
            if (p.name === pipelineName) {
              p.lambdas.forEach((l) => allLambdaIdsToToggle.push(l.id));
            }
          });
        });
      }

      const allSelected = allLambdaIdsToToggle.every((id) => selectedLambdaIdsRef.current.has(id));

      startTransition(() => {
        setSelectedLambdaIds((prev) => {
          const next = new Set(prev);
          if (allSelected) {
            allLambdaIdsToToggle.forEach((id) => next.delete(id));
          } else {
            allLambdaIdsToToggle.forEach((id) => next.add(id));
          }
          return next;
        });
        setSelectionVersion((v) => v + 1);
      });
    },
    [accounts]
  );

  /** Collect all lambda IDs for a pipeline (respecting Manually created scoping). */
  const collectPipelineLambdaIds = useCallback(
    (pipeline: Pipeline): string[] => {
      const ids: string[] = [];
      if (pipeline.name === 'Manually created') {
        pipeline.lambdas.forEach((l) => ids.push(l.id));
      } else {
        accounts.forEach((account) => {
          account.pipelines.forEach((p) => {
            if (p.name === pipeline.name) {
              p.lambdas.forEach((l) => ids.push(l.id));
            }
          });
        });
      }
      return ids;
    },
    [accounts]
  );

  const isPipelineSelected = useCallback(
    (pipeline: Pipeline) => {
      const ids = collectPipelineLambdaIds(pipeline);
      return ids.length > 0 && ids.every((id) => selectedLambdaIdsRef.current.has(id));
    },
    [collectPipelineLambdaIds]
  );

  const isPipelinePartiallySelected = useCallback(
    (pipeline: Pipeline) => {
      const ids = collectPipelineLambdaIds(pipeline);
      const selectedCount = ids.filter((id) => selectedLambdaIdsRef.current.has(id)).length;
      return selectedCount > 0 && selectedCount < ids.length;
    },
    [collectPipelineLambdaIds]
  );

  const clearSelection = useCallback(() => {
    setSelectedLambdaIds(new Set());
  }, []);

  // Selected lambdas with pipeline info
  const selectedLambdasWithPipeline = useMemo(() => {
    const result: Array<{ lambda: LambdaFunction; pipelineName: string; isManual: boolean }> = [];
    accounts.forEach((account) => {
      account.pipelines.forEach((pipeline) => {
        pipeline.lambdas.forEach((lambda) => {
          if (selectedLambdaIds.has(lambda.id)) {
            const isManual = pipeline.name === 'Manually created' || pipeline.name === 'Manual Stack deployment';
            result.push({ lambda, pipelineName: pipeline.name, isManual });
          }
        });
      });
    });
    return result;
  }, [accounts, selectedLambdaIds]);

  const selectedLambdas = useMemo(() => selectedLambdasWithPipeline.map((item) => item.lambda), [selectedLambdasWithPipeline]);

  // Delete eligibility: only manual lambdas or deleted pipelines
  const canDeleteSelected = useMemo(() => {
    if (selectedLambdasWithPipeline.length === 0) return false;
    return selectedLambdasWithPipeline.every((item) => item.isManual || item.pipelineName.toLowerCase().includes('(deleted)'));
  }, [selectedLambdasWithPipeline]);

  const manualLambdaCount = useMemo(() => selectedLambdasWithPipeline.filter((item) => item.isManual).length, [selectedLambdasWithPipeline]);

  const deletedPipelineLambdaCount = useMemo(
    () => selectedLambdasWithPipeline.filter((item) => item.pipelineName.toLowerCase().includes('(deleted)')).length,
    [selectedLambdasWithPipeline]
  );

  const pipelineLambdaCount = useMemo(
    () => selectedLambdasWithPipeline.filter((item) => !item.isManual && !item.pipelineName.toLowerCase().includes('(deleted)')).length,
    [selectedLambdasWithPipeline]
  );

  const deletedPipelineNames = useMemo(() => {
    const names = new Set<string>();
    selectedLambdasWithPipeline.forEach((item) => {
      if (item.pipelineName.toLowerCase().includes('(deleted)')) {
        names.add(item.pipelineName);
      }
    });
    return Array.from(names);
  }, [selectedLambdasWithPipeline]);

  // Stack names from manually created/deployed lambdas that have a CFN stack
  const manualStackNames = useMemo(() => {
    const names = new Set<string>();
    selectedLambdasWithPipeline.forEach((item) => {
      if (item.isManual && item.lambda.stackName && item.lambda.stackName !== 'Manually created (no stack)') {
        names.add(item.lambda.stackName);
      }
    });
    return Array.from(names);
  }, [selectedLambdasWithPipeline]);

  // Standalone manual lambdas without a CFN stack
  const manualStandaloneLambdas = useMemo(
    () =>
      selectedLambdasWithPipeline.filter(
        (item) => item.isManual && (!item.lambda.stackName || item.lambda.stackName === 'Manually created (no stack)')
      ),
    [selectedLambdasWithPipeline]
  );

  // Get unique pipeline names from selected lambdas
  const getSelectedPipelines = useCallback((): string[] => {
    const pipelineNames = new Set<string>();
    accounts.forEach((account) => {
      account.pipelines.forEach((pipeline) => {
        const hasSelectedLambda = pipeline.lambdas.some((l) => selectedLambdaIds.has(l.id));
        if (hasSelectedLambda) {
          pipelineNames.add(pipeline.name);
        }
      });
    });
    return Array.from(pipelineNames);
  }, [selectedLambdaIds, accounts]);

  // Language counts for version modal
  const selectedLanguageCounts = useMemo(() => getLambdaCountByLanguage(selectedLambdas), [selectedLambdas]);

  return {
    selectedLambdaIds,
    setSelectedLambdaIds,
    selectedLambdaIdsRef,
    selectionVersion,
    toggleLambdaSelection,
    togglePipelineSelection,
    isPipelineSelected,
    isPipelinePartiallySelected,
    clearSelection,
    selectedLambdas,
    selectedLambdasWithPipeline,
    canDeleteSelected,
    manualLambdaCount,
    deletedPipelineLambdaCount,
    pipelineLambdaCount,
    deletedPipelineNames,
    manualStackNames,
    manualStandaloneLambdas,
    getSelectedPipelines,
    selectedLanguageCounts
  };
};
